// Объекты с подписью: рисунок (4.5), таблица (4.9) с примечанием (4.10.7), листинг (6.1).
// Подпись - узел caption (редактируется прямо в документе); «Рисунок 3. » перед ней ставит плагин нумерации.
import { Node, mergeAttributes } from '@tiptap/core';
import { Fragment } from '@tiptap/pm/model';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { idAttr, jsonAttr, registryNodeView } from './util';
import { appStore } from '../store';

/** Подпись рисунка / название таблицы / название листинга. */
export const Caption = Node.create({
  name: 'caption',
  content: 'inline*',
  defining: true,
  parseHTML() { return [{ tag: 'figcaption' }, { tag: 'caption' }, { tag: 'div.sto-caption' }]; },
  renderHTML({ HTMLAttributes }) { return ['div', mergeAttributes(HTMLAttributes, { class: 'sto-caption' }), 0]; },
});

export function assetUrl(name: string, thumb = 0): string {
  const pid = appStore.getState().projectId || '';
  return `/api/projects/${encodeURIComponent(pid)}/assets/${encodeURIComponent(name)}${thumb ? `?thumb=${thumb}` : ''}`;
}

/** Рисунок: изображение из assets проекта + подпись. width: '15cm' | '80%' | null (натуральный размер). */
export const Figure = Node.create({
  name: 'figure',
  group: 'block',
  content: 'caption',
  draggable: true,
  isolating: true,
  addAttributes() {
    return {
      ...idAttr(),
      src: { default: '', parseHTML: (el) => el.getAttribute('data-src') || '', renderHTML: (a) => ({ 'data-src': a.src }) },
      width: { default: null, parseHTML: (el) => el.getAttribute('data-width'), renderHTML: (a) => (a.width ? { 'data-width': a.width } : {}) },
      alt: { default: '', parseHTML: (el) => el.getAttribute('data-alt') || '', renderHTML: (a) => (a.alt ? { 'data-alt': a.alt } : {}) },
      /** заимствованный рисунок: {source: id источника, loc: 'с. 5'} -> «… [3, с. 5]» в подписи */
      borrowed: jsonAttr<{ source: string; loc?: string } | null>('borrowed', null),
      /** изображения нет (импорт .docx): 'chart' | 'shapes' | 'smartart' | 'ink' | 'ole' | 'no-image' - в отчёте
       *  рамка «Рисунок не перенесён», проверка - E FIG-IMAGE-MISSING; исчезает, когда вставлено изображение */
      missing: { default: null, parseHTML: (el) => el.getAttribute('data-missing'), renderHTML: (a) => (a.missing ? { 'data-missing': a.missing } : {}) },
      /** несколько изображений под одной подписью: [{src, width, label: 'а)'}] (src первого = src рисунка) */
      images: jsonAttr<Array<{ src: string; width?: string | null; label?: string }> | null>('images', null),
      /** расположение частей: 'row' - рядом, 'column' - друг под другом, 'grid' - сеткой по cols; null - по размерам */
      layout: { default: null, parseHTML: (el) => el.getAttribute('data-layout'), renderHTML: (a) => (a.layout ? { 'data-layout': a.layout } : {}) },
      /** колонок в сетке частей (1-6) */
      cols: { default: null, parseHTML: (el) => (el.getAttribute('data-cols') ? Number(el.getAttribute('data-cols')) : null), renderHTML: (a) => (a.cols ? { 'data-cols': String(a.cols) } : {}) },
      /** пояснения к обозначениям на рисунке (4.5.7: над подписью) - строчные узлы */
      expl: jsonAttr<any[] | null>('expl', null),
    };
  },
  parseHTML() { return [{ tag: 'figure[data-type="figure"]' }]; },
  renderHTML({ node, HTMLAttributes }) {
    return ['figure', mergeAttributes(HTMLAttributes, { 'data-type': 'figure', class: 'sto-figure' }),
      ['div', { class: 'sto-figure-img', contenteditable: 'false' },
        node.attrs.src ? ['img', { src: assetUrl(node.attrs.src), alt: node.attrs.alt || '', draggable: 'false' }] : ['span', 'нет изображения']],
      ['div', { class: 'sto-figure-cap' }, 0]];
  },
  addNodeView: registryNodeView('figure'),
});

/** Строчный рисунок - картинка в строке текста ячейки таблицы (значок, график, схема в графе «Схема»): импорт из
 *  Word (docx_import.cell_images), в DOCX - «![](…){inline width=…}», вписывается в ширину графы. width: '3.2cm'. */
export const InlineImage = Node.create({
  name: 'inlineImage',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      src: { default: '', parseHTML: (el) => el.getAttribute('data-src') || '', renderHTML: (a) => ({ 'data-src': a.src }) },
      width: { default: null, parseHTML: (el) => el.getAttribute('data-width'), renderHTML: (a) => (a.width ? { 'data-width': a.width } : {}) },
      alt: { default: '', parseHTML: (el) => el.getAttribute('data-alt') || '', renderHTML: (a) => (a.alt ? { 'data-alt': a.alt } : {}) },
    };
  },
  parseHTML() { return [{ tag: 'img[data-type="inline-image"]' }]; },
  renderHTML({ node, HTMLAttributes }) {
    const w = /^\d+(\.\d+)?cm$/.test(String(node.attrs.width || '')) ? `width:${node.attrs.width};` : '';
    return ['img', mergeAttributes(HTMLAttributes, { 'data-type': 'inline-image', class: 'sto-inline-image',
      src: node.attrs.src ? assetUrl(node.attrs.src) : '', alt: node.attrs.alt || '', draggable: 'false',
      style: `${w}max-width:100%;height:auto;vertical-align:middle` })];
  },
  renderText() { return ''; },
});

// --- таблица: обёртка stoTable = [название] + таблица prosemirror-tables + [примечание]
const alignAttr = {
  align: {
    default: null as null | 'left' | 'center' | 'right',
    parseHTML: (el: HTMLElement) => (el.getAttribute('data-align') || el.style.textAlign || null) as any,
    renderHTML: (a: Record<string, any>) => (a.align ? { 'data-align': a.align, style: `text-align:${a.align}` } : {}),
  },
};

export const StoTableCell = TableCell.extend({
  content: 'paragraph+',
  addAttributes() { return { ...this.parent?.(), ...alignAttr }; },
});
export const StoTableHeader = TableHeader.extend({
  content: 'paragraph+',
  addAttributes() { return { ...this.parent?.(), ...alignAttr }; },
});
/** Таблица prosemirror-tables живёт только внутри stoTable (не отдельным блоком). */
export const StoInnerTable = Table.extend({ group: 'stoTableInner' });

export const TableNote = Node.create({
  name: 'tableNote',
  content: 'paragraph+',
  defining: true,
  parseHTML() { return [{ tag: 'div.sto-table-note' }]; },
  renderHTML({ HTMLAttributes }) { return ['div', mergeAttributes(HTMLAttributes, { class: 'sto-table-note' }), 0]; },
});

export const StoTable = Node.create({
  name: 'stoTable',
  group: 'block',
  content: 'caption? table tableNote?',
  isolating: true,
  draggable: false,
  addAttributes() {
    return {
      ...idAttr(),
      /** ширины граф в процентах (иначе - по содержимому или colwidth ячеек) */
      widths: jsonAttr<number[] | null>('widths', null),
    };
  },
  parseHTML() { return [{ tag: 'div[data-type="sto-table"]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'sto-table', class: 'sto-table' }), 0];
  },
  addNodeView: registryNodeView('stoTable'),
});

/** Листинг: название + код (атрибут code, редактор CodeMirror в node view). */
export const Listing = Node.create({
  name: 'listing',
  group: 'block',
  content: 'caption',
  isolating: true,
  draggable: true,
  addAttributes() {
    return {
      ...idAttr(),
      lang: { default: '', parseHTML: (el) => el.getAttribute('data-lang') || '', renderHTML: (a) => (a.lang ? { 'data-lang': a.lang } : {}) },
      code: {
        default: '',
        parseHTML: (el) => el.querySelector('pre')?.textContent ?? el.textContent ?? '',
        renderHTML: () => ({}),
      },
    };
  },
  parseHTML() {
    return [
      { tag: 'div[data-type="listing"]', contentElement: '.sto-listing-cap' },
      { tag: 'pre', priority: 40, getContent: (_dom, schema) => Fragment.from(schema.nodes.caption.create()),
        getAttrs: (el) => ({ code: (el as HTMLElement).textContent || '' }) },
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'listing', class: 'sto-listing' }),
      ['div', { class: 'sto-listing-cap' }, 0],
      ['pre', { class: 'sto-listing-code', contenteditable: 'false' }, node.attrs.code || ' ']];
  },
  addNodeView: registryNodeView('listing'),
});

export const objectExtensions = () => [
  Caption, Figure, StoTable,
  StoInnerTable.configure({ resizable: true, cellMinWidth: 40, allowTableNodeSelection: true }),
  TableRow, StoTableCell, StoTableHeader, TableNote, Listing, InlineImage,
];

/** Объекты на листах «Задание»/«Реферат» (малый редактор, core/editor/MiniEditor): рисунок, таблица, листинг - те же
 *  узлы, что в тексте, но без отображений основного редактора (ручек, лент): их правят в Word-подобном виде -
 *  подпись и ячейки редактируются, сам объект переносится как есть (импорт из Word его не теряет). */
export const sheetObjectExtensions = () => [
  Caption, Figure.extend({ addNodeView() { return null as any; } }), StoTable,
  StoInnerTable.configure({ resizable: false, allowTableNodeSelection: true }),
  TableRow, StoTableCell, StoTableHeader, TableNote, Listing.extend({ addNodeView() { return null as any; } }), InlineImage,
];
